import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Inject,
  Input,
  OnDestroy,
  OnInit,
  Output
} from "@angular/core";
import {
  Conversation,
  ConversationUpdateReason,
  Message,
  MessageUpdateReason,
  Paginator,
  Participant,
  ParticipantUpdateReason,
  User
} from "@twilio/conversations";
import {ChatService} from "../../services/chat.service";
import {Subscription} from "rxjs";
import {BringozChatConversationModel} from "../../models/chat-conversation.model";
import {ChatHttpService} from "../../services/chat-http.service";
import {CurrentSiteContextService} from "../../../commons/angular/services/current-site-context.service";
import {ChatUserModel} from "../../models/chat-user.model";
import {ChatUserRoleType} from "../../types/chat-user-role.type";

require('./chat-conversation-view.scss');
@Component({
  standalone: false,
  selector: 'chat-conversation-view',
  templateUrl: './chat-conversation-view.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ChatConversationViewComponent implements OnInit, OnDestroy {

  @Input() conversation: Conversation;
  @Input() enableBack?: boolean;
  @Input() enableClose?: boolean;
  @Output() onBack: EventEmitter<Conversation> = new EventEmitter();
  @Output() onClose: EventEmitter<Conversation> = new EventEmitter();

  public color: string;
  public isServiceConversation: boolean;
  public conversationUsers: User[] = [];
  public editingConversationUsers: any[];
  public pageSize = 100;
  public messagesPaginator: Paginator<Message>
  public text: string;
  public media: any;
  public usersTyping: User[] = [];

  private muteConversationUsersSubscription: Subscription;

  private siteId: string;
  private currentUser: User;
  private messageAddedSubscription: any;
  private messageRemovedSubscription: any;
  private messageUpdatedSubscription: any;
  private participantJoinedSubscription: any;
  private participantLeftSubscription: any;
  private participantUpdatedSubscription: any;
  private typingStartedSubscription: any;
  private typingEndedSubscription: any;
  private updatedSubscription: any;
  private removedSubscription: any;

  constructor(@Inject(ChatService) private chatService: ChatService,
              @Inject(ChatHttpService) private chatHttpService: ChatHttpService,
              @Inject(CurrentSiteContextService) private currentSiteContextService: CurrentSiteContextService,
              @Inject(ChangeDetectorRef) private changeDetectorRef: ChangeDetectorRef) {

  }

  ngOnInit(): void {
    this.siteId = this.currentSiteContextService.currentSiteId;
    this.currentUser = this.chatService.getCurrentUser();
    this.color = this.chatService.getColorById(this.conversation.sid);
    this.isServiceConversation = !!this.conversation.attributes && !!this.conversation.attributes['serviceId'];
    this._getMessages(this.pageSize);
    this._getParticipants();
    this._initConversationSubscriptions();
  }

  ngOnDestroy(): void {

    if (this.muteConversationUsersSubscription) {
      this.muteConversationUsersSubscription.unsubscribe();
    }

    this.conversation.removeAllListeners();
    // if (this.messageAddedSubscription) {
    //   this.messageAddedSubscription.removeListener('messageAdded', undefined);
    // }
    // if (this.messageRemovedSubscription) {
    //   this.messageRemovedSubscription.off();
    // }
    // if (this.messageUpdatedSubscription) {
    //   this.messageUpdatedSubscription.off();
    // }
    // if (this.participantJoinedSubscription) {
    //   this.participantJoinedSubscription.off();
    // }
    // if (this.participantLeftSubscription) {
    //   this.participantLeftSubscription.off();
    // }
    // if (this.participantUpdatedSubscription) {
    //   this.participantUpdatedSubscription.off();
    // }
    // if (this.typingStartedSubscription) {
    //   this.typingStartedSubscription.off();
    // }
    // if (this.typingEndedSubscription) {
    //   this.typingEndedSubscription.off();
    // }
    // if (this.updatedSubscription) {
    //   this.updatedSubscription.off();
    // }
    // if (this.removedSubscription) {
    //   this.removedSubscription.off();
    // }
  }

  sendMessage() {
    if (!this.text && !this.media) {
      return;
    } else {
      const messageToSend = this.conversation.prepareMessage()

      if (this.text) {
        messageToSend.setBody(this.text);
      }

      if (this.media) {
        messageToSend.addMedia(this.media)
      }

      messageToSend.build().send()
      this.text = undefined;
      this.media = undefined;

      if (!!this.isServiceConversation && (!this.conversation.attributes || !this.conversation.attributes['assignedToAdmin'])) {
        this._setUsersToMute();
      }

    }
  }

  onUploadMedia(event) {
    const file = event.target.files[0];
    this.media = {
      contentType: file.type,
      filename: file.name,
      media: file
    }
  }

  onTyping(e) {
    const keyCode = e.which || e.keyCode;
    if (keyCode !== 13) {
      this.conversation.typing();
    }
  }

  onEnableEditingUsers() {
    this.editingConversationUsers = [...this.conversationUsers];
    // this.editingConversationUsers = this.conversationUsers.concat(this.conversationUsers).concat(this.conversationUsers);
  }

  onDisableEditingUsers() {
    this.editingConversationUsers = undefined;
  }

  onRemoveEditingUser(user: User) {
    this.editingConversationUsers = this.editingConversationUsers.filter((u: User) => {
      return u.identity !== user.identity;
    })
  }

  onAddEditingUser(user: ChatUserModel) {
    if (user.maskedId === this.currentUser.identity) {
      return;
    }

    const currentIndex = this.editingConversationUsers.findIndex((u: User) => {
      return u.identity === user.maskedId;
    })
    if (currentIndex === -1) {
      this.editingConversationUsers = this.editingConversationUsers.concat([user]);
    }
  }

  onSaveEditingUsers() {
    const participantsToAdd = this.editingConversationUsers.filter((user: any) => {
      return !!user.id;
    });
    const participantsToRemove = this.conversationUsers.filter((user) => {
      return !this.editingConversationUsers.find((u) => {
        return u.identity === user.identity;
      })
    });

    const chatConversation: any = {
      participants: participantsToAdd,
      participantsToRemove: participantsToRemove.map((u) => {
        return u.identity;
      })
    };
    this.chatHttpService.updateConversation(this.siteId, this.conversation.sid, chatConversation)
        .subscribe((c: BringozChatConversationModel) => {
          this.editingConversationUsers = undefined;
        })
  }

  private _getMessages(pageSize?: number, anchor?: number, direction?: "backwards" | "forward") {
    this.conversation.getMessages(pageSize)
      .then((messagesPaginator: Paginator<Message>) => {

        if (messagesPaginator?.items?.length) {
          const lastMessage = messagesPaginator.items[messagesPaginator.items.length - 1];
          this.conversation.updateLastReadMessageIndex(lastMessage.index);
        }

        messagesPaginator.items = messagesPaginator.items.reverse();
        this.messagesPaginator = messagesPaginator;

        this._checkDetector();
      })
  }

  private _getParticipants() {
    this.conversation.getParticipants()
      .then((participants: Participant[]) => {
        this._checkDetector();

        participants.forEach((p: Participant, i) => {
          p.getUser()
            .then((user: User) => {
              if (this.currentUser.identity !== user.identity) {
                this.conversationUsers = this.conversationUsers.concat([user]);
              }
              this._checkDetector();
            })
        })

      })
  }

  private _setUsersToMute() {
    this.conversation.getParticipants()
      .then((participants: Participant[]) => {

        const usersIdentityToMute = [];
        participants.forEach((p: Participant, i) => {
          p.getUser()
            .then((user: User) => {
              if (this.currentUser.identity !== user.identity) {
                if (user.attributes && user.attributes['role'] === ChatUserRoleType.Admin) {
                  usersIdentityToMute.push(user.identity);
                }
              }

              if (participants.length === (i + 1) && usersIdentityToMute.length) {
                this._muteUsers(usersIdentityToMute);
              }
            })
        })

      })
  }

  private _muteUsers(muteIds: string[]) {
    this.muteConversationUsersSubscription = this.chatHttpService.muteConversationUsers(
      this.siteId,
      this.conversation.sid,
      muteIds,
      [],
      true
    )
      .subscribe(() => {
        this._checkDetector();
      })
  }

  private _initConversationSubscriptions() {
    this.messageAddedSubscription = this.conversation.on("messageAdded", (message: Message) => {
      this.messagesPaginator.items = [message, ...this.messagesPaginator.items];
      this.conversation.setAllMessagesRead();
      this._checkDetector();
    });

    this.messageRemovedSubscription = this.conversation.on("messageRemoved", (message: Message) => {
      this.messagesPaginator.items = [...this.messagesPaginator.items.filter((m) => m.sid !== message.sid)];
      this._checkDetector();
    });

    this.messageUpdatedSubscription = this.conversation.on("messageUpdated", (data: { message: Message, updateReasons: MessageUpdateReason[] }) => {
      const messageIndex = this.messagesPaginator.items.findIndex((m) => m.sid === data.message.sid);
      if (messageIndex > -1) {
        this.messagesPaginator.items.splice(messageIndex, 1, data.message);
        this._checkDetector();
      }
    });

    this.participantJoinedSubscription = this.conversation.on("participantJoined", (participant: Participant) => {
      participant.getUser()
          .then((user: User) => {
            this.conversationUsers = this.conversationUsers.concat([user]);
            this._checkDetector();
          })
    });

    this.participantLeftSubscription = this.conversation.on("participantLeft", (participant: Participant) => {
      participant.getUser()
          .then((user: User) => {
            this.conversationUsers = this.conversationUsers.filter((u: User) => {
              return u.identity !== user.identity;
            });
            this._checkDetector();
          })
    });

    this.participantUpdatedSubscription = this.conversation.on("participantUpdated", (data: { participant: Participant, updateReasons: ParticipantUpdateReason[] }) => {
    });

    this.typingStartedSubscription = this.conversation.on("typingStarted", (participant: Participant) => {
      participant.getUser()
        .then((user: User) => {
          const currentIndex = this.usersTyping.findIndex((u: User) => {
            return u.identity === user.identity;
          })
          if (currentIndex === -1) {
            this.usersTyping = this.usersTyping.concat([user]);
            this._checkDetector();
          }
        })
    });

    this.typingEndedSubscription = this.conversation.on("typingEnded", (participant: Participant) => {
      participant.getUser()
        .then((user: User) => {
          this.usersTyping = this.usersTyping.filter((u: User) => {
            return u.identity !== user.identity;
          })
          this._checkDetector();
        })
    });

    this.updatedSubscription = this.conversation.on("updated", (data: { conversation: Conversation, updateReasons: ConversationUpdateReason[] }) => {
    });

    this.removedSubscription = this.conversation.on("removed", (conversation: Conversation) => {
      this.onBack.emit(conversation);
    });
  }

  private _checkDetector() {
    this.changeDetectorRef.detectChanges();
  }

}
